import { useCallback, useRef, useState } from "react"

/**
 * Tracks WebView navigation history manually.
 *
 * On Android, the `canGoBack` / `canGoForward` values reported by
 * react-native-webview events are unreliable — they can lag behind the actual
 * history state. This hook maintains its own URL stack and derives
 * canGoBack/canGoForward from it instead.
 */
export function useWebViewNavigation(webViewRef: React.RefObject<any>) {
  const stackRef = useRef<string[]>([])
  const indexRef = useRef(-1)
  const isProgrammaticNavRef = useRef(false)

  const [canGoBack, setCanGoBack] = useState(false)
  const [canGoForward, setCanGoForward] = useState(false)
  const [currentUrl, setCurrentUrl] = useState("")

  const updateState = useCallback((index: number, stack: string[]) => {
    setCanGoBack(index > 0)
    setCanGoForward(index < stack.length - 1)
    setCurrentUrl(stack[index] ?? "")
  }, [])

  const pushUrl = useCallback(
    (url: string) => {
      const stack = stackRef.current
      const index = indexRef.current
      if (url === stack[index]) return
      const newStack = stack.slice(0, index + 1)
      newStack.push(url)
      stackRef.current = newStack
      indexRef.current = newStack.length - 1
      updateState(indexRef.current, newStack)
    },
    [updateState],
  )

  const onNavigationStateChange = useCallback(
    (navState: { url: string; loading: boolean }) => {
      if (navState.loading || !navState.url) return
      if (isProgrammaticNavRef.current) {
        isProgrammaticNavRef.current = false
        updateState(indexRef.current, stackRef.current)
        return
      }
      pushUrl(navState.url)
    },
    [pushUrl, updateState],
  )

  const goBack = useCallback(() => {
    if (indexRef.current <= 0) return
    isProgrammaticNavRef.current = true
    indexRef.current -= 1
    updateState(indexRef.current, stackRef.current)
    webViewRef.current?.goBack()
  }, [webViewRef, updateState])

  const goForward = useCallback(() => {
    if (indexRef.current >= stackRef.current.length - 1) return
    isProgrammaticNavRef.current = true
    indexRef.current += 1
    updateState(indexRef.current, stackRef.current)
    webViewRef.current?.goForward()
  }, [webViewRef, updateState])

  const webViewProps = {
    onNavigationStateChange,
  }

  return { canGoBack, canGoForward, currentUrl, goBack, goForward, webViewProps }
}
